import { ApiRequestError } from '@access-cms/api-client';

const LABOR_CATEGORY_DUPLICATE_CODE = '409.EM.CONTRACT.2002';

export const LABOR_CATEGORY_DUPLICATE_NAME_MESSAGE =
  'A labor category with this name already exists on this contract.\n\nEnter a different name or edit the existing category.';

const LABOR_CATEGORY_ERROR_MESSAGES: Record<string, string> = {
  [LABOR_CATEGORY_DUPLICATE_CODE]: LABOR_CATEGORY_DUPLICATE_NAME_MESSAGE,
};

const PDT_STILL_PROCESSING_MESSAGE = 'Previous PDT upload still processing, please wait';

const LABOR_CATEGORY_SAVE_FAILURE_SUFFIX = [
  'Please try again later or contact the CMS Service Desk:',
  '1-800-562-1963',
  '410-786-2580',
  'CMS_IT_Service_Desk@cms.hhs.gov',
].join(' ');

function getStructuredErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiRequestError)) {
    return undefined;
  }

  return typeof error.code === 'string' ? error.code : undefined;
}

function isDuplicateLaborCategoryConflict(error: unknown): boolean {
  const errorCode = getStructuredErrorCode(error);
  if (errorCode === LABOR_CATEGORY_DUPLICATE_CODE) {
    return true;
  }

  if (error instanceof ApiRequestError && error.statusCode === 409) {
    const message = error.message.toLowerCase();

    return message.includes('already exists') && message.includes('labor category');
  }

  return false;
}

function getErrorDetail(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return '';
}

function isPdtStillProcessingError(error: unknown): boolean {
  return getErrorDetail(error).toLowerCase().includes('previous pdt upload still processing');
}

/**
 * Returns true when `name` matches another active LCAT on the contract (case/trim insensitive).
 * Pass `excludeId` when editing so the current category does not conflict with itself.
 */
export function hasActiveLaborCategoryNameConflict(
  name: string,
  laborCategories: Array<{ categoryName?: string | null; id?: string }>,
  excludeId?: string,
): boolean {
  const normalized = name.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return laborCategories.some((category) => {
    if (excludeId && category.id === excludeId) {
      return false;
    }

    return (category.categoryName ?? '').trim().toLowerCase() === normalized;
  });
}

export function getLaborCategoryPdtUploadErrorMessage(context: {
  categoryName: string;
  error?: unknown;
  riskLevelDisplayName: string;
}): string {
  const prefix = `${context.categoryName}, ${context.riskLevelDisplayName} was saved, but the PDT file could not be uploaded.`;

  if (context.error && isPdtStillProcessingError(context.error)) {
    return `${prefix} ${PDT_STILL_PROCESSING_MESSAGE}`;
  }

  return [prefix, LABOR_CATEGORY_SAVE_FAILURE_SUFFIX].join(' ');
}

export function getLaborCategorySaveErrorMessage(
  error: unknown,
  context: { action: 'add' | 'update'; categoryName: string; riskLevelDisplayName: string },
): string {
  if (isDuplicateLaborCategoryConflict(error)) {
    return LABOR_CATEGORY_ERROR_MESSAGES[LABOR_CATEGORY_DUPLICATE_CODE];
  }

  const errorCode = getStructuredErrorCode(error);
  const mappedMessage = errorCode ? LABOR_CATEGORY_ERROR_MESSAGES[errorCode] : undefined;
  if (mappedMessage) {
    return mappedMessage;
  }

  const detail = getErrorDetail(error);
  if (detail) {
    const actionVerb = context.action === 'add' ? 'added' : 'updated';

    return `${context.categoryName}, ${context.riskLevelDisplayName} could not be ${actionVerb}:\n${detail}`;
  }

  const actionVerb = context.action === 'add' ? 'added' : 'updated';

  return [
    `${context.categoryName}, ${context.riskLevelDisplayName} could not be ${actionVerb}.`,
    LABOR_CATEGORY_SAVE_FAILURE_SUFFIX,
  ].join('\n\n');
}
